import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useCVGenerationStore } from '../stores/cvGenerationStore'
import { api } from '../lib/api'
import JDInputStep from '../components/cv-generation/JDInputStep'
import InterrogationStep from '../components/cv-generation/InterrogationStep'
import ReviewStep from '../components/cv-generation/ReviewStep'
import ExportStep from '../components/cv-generation/ExportStep'
import QuotaGate from '../components/QuotaGate'
import Sidebar from '../components/Sidebar'

const STEPS = [
  { id: 'jd_input', label: 'JD Input' },
  { id: 'interrogation', label: 'Guided Questions' },
  { id: 'review', label: 'Review & Edit' },
  { id: 'export', label: 'Export' },
]

function stepIndex(currentStep: string): number {
  if (currentStep === 'cv.jd_input' || currentStep === 'cv.analysis') return 0
  if (currentStep === 'cv.interrogation') return 1
  if (currentStep === 'cv.assembly' || currentStep === 'cv.review' || currentStep === 'cv.editing') return 2
  if (currentStep === 'cv.approved') return 3
  return 0
}

export default function CVGenerationPage() {
  const { user, signOut } = useAuthStore()
  const { session, error, quotaGate, fetchActiveSession, fetchSession, clearError, reset, resumeAfterKeyFix } = useCVGenerationStore()
  const [searchParams] = useSearchParams()
  const resumeId = searchParams.get('session')
  const [libraryChecked, setLibraryChecked] = useState(false)
  const [libraryEmpty, setLibraryEmpty] = useState(false)

  useEffect(() => {
    if (resumeId && !session) {
      fetchSession(resumeId)
    } else if (!resumeId && !session) {
      fetchActiveSession()
    }
  }, [resumeId, session, fetchSession, fetchActiveSession])

  useEffect(() => {
    if (resumeId) {
      setLibraryChecked(true) // resuming a session — skip the check
      return
    }
    api.get<{ data: unknown[] }>('/api/v1/library')
      .then(res => {
        setLibraryEmpty(res.data.length === 0)
        setLibraryChecked(true)
      })
      .catch(() => { setLibraryChecked(true) }) // on error, unblock the form
  }, [resumeId])

  useEffect(() => {
    return () => { reset() }
  }, [reset])

  const currentStepIdx = session ? stepIndex(session.current_step) : 0

  function renderStep() {
    if (!session) {
      if (libraryChecked && libraryEmpty) {
        return (
          <div className="max-w-lg mx-auto mt-16 text-center">
            <div className="w-12 h-12 rounded-full bg-[#f5ede9] flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-[#833c0b]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10.5v6m0 0H9m3 0h3M6.75 19.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-[#1a1c1a] mb-2">Your library is empty</h3>
            <p className="text-sm text-[#545f6e] mb-6">
              Add at least one Proof to your library before generating a CV.
            </p>
            <Link
              to="/library"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#833c0b] text-white text-sm rounded-lg hover:bg-[#6b3009] transition-colors"
            >
              Go to Library
            </Link>
          </div>
        )
      }
      return <JDInputStep disabled={!libraryChecked} />
    }

    switch (session.current_step) {
      case 'cv.jd_input':
      case 'cv.analysis':
        return <JDInputStep />
      case 'cv.interrogation':
        return <InterrogationStep />
      case 'cv.assembly':
      case 'cv.review':
      case 'cv.editing':
        return <ReviewStep />
      case 'cv.approved':
        return <ExportStep />
      default:
        return <JDInputStep />
    }
  }

  return (
    <div className="flex min-h-screen bg-[#faf9f6]">
      {/* Sidebar */}
      <Sidebar context="cv-generation" />

      {/* Main content */}
      <div className="flex-1 ml-16 lg:ml-64 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-[#faf9f6]/90 backdrop-blur-md border-b border-[#dac2b6]/20">
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-[#1a1c1a] font-['Inter']">
                Generate CV
              </h2>
              <p className="text-xs text-[#545f6e] mt-0.5">
                Step {currentStepIdx + 1} of {STEPS.length}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-[#545f6e]">{user?.email}</span>
              <button
                onClick={signOut}
                className="text-xs text-[#545f6e] hover:text-[#1a1c1a] transition-colors"
              >
                Sign out
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="px-6 pb-3">
            <div className="flex gap-2">
              {STEPS.map((step, idx) => (
                <div key={step.id} className="flex-1">
                  <div
                    className={`h-1 rounded-full transition-colors ${
                      idx <= currentStepIdx ? 'bg-[#833c0b]' : 'bg-[#e3e2df]'
                    }`}
                  />
                  <p
                    className={`text-[10px] mt-1 ${
                      idx <= currentStepIdx
                        ? 'text-[#1a1c1a] font-medium'
                        : 'text-[#545f6e]'
                    }`}
                  >
                    {step.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </header>

        {/* Quota gate — a hit cap is a paywall, not a crash; show actionable CTAs */}
        {quotaGate && (
          <div className="mx-6 mt-4 px-4 py-6 bg-surface-container-low border border-outline-variant/30 rounded-lg">
            <QuotaGate gate={quotaGate} variant="card" onKeyResolved={resumeAfterKeyFix} />
          </div>
        )}

        {/* Error banner */}
        {!quotaGate && error && (
          <div className="mx-6 mt-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm text-red-700">{error}</p>
              {session?.session_id && (
                <p className="mt-1 text-xs text-red-400 font-mono break-all">
                  session: {session.session_id}
                </p>
              )}
            </div>
            <button
              onClick={clearError}
              className="text-red-400 hover:text-red-600 text-sm shrink-0"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Step content */}
        <main className="flex-1 px-6 py-8">
          {renderStep()}
        </main>

        {/* Footer */}
        <footer className="py-3 text-center text-xs text-[#545f6e] border-t border-[#dac2b6]/20">
          <a href="/privacy" className="hover:text-[#1a1c1a] transition-colors">
            Privacy
          </a>
        </footer>
      </div>
    </div>
  )
}
